import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChartBar, ArrowLeft, TrendUp, TrendDown, Check, X as XIcon } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { PageShell } from "@/components/PageShell";
import { t } from "@/lib/i18n";

export default function PriorityReport() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data: d } = await api.get("/priority/report");
      setData(d);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resolveConflict = async (memory_id, action) => {
    try {
      await api.post("/priority/conflict", { memory_id, action });
      toast.success(action === "remove" ? t("pi.rp_conflict_removed", "Exception removed") : t("pi.rp_conflict_kept", "Exception kept"));
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const latest = data?.latest;
  const history = (data?.history || []).slice().reverse();

  return (
    <PageShell title={t("pi.rp_title", "Priority report")}
      subtitle={t("pi.rp_sub", "How well the ranking matched what you actually did — measured weekly, in your timezone.")} testId="priority-report-page">
      <button data-testid="report-back-btn" onClick={() => navigate("/priority")}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-800 mb-5 -mt-2">
        <ArrowLeft size={13} /> {t("pi.rp_back", "Back to Priority")}
      </button>

      {loading && <p className="text-sm text-gray-400">{t("common.loading", "Loading…")}</p>}

      {!loading && !latest && (
        <div data-testid="report-empty" className="border border-dashed border-gray-300 rounded-md p-8 max-w-lg text-center">
          <ChartBar size={26} className="text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">{t("pi.rp_none", "No weekly report yet — your first one appears after your first full week.")}</p>
        </div>
      )}

      {latest && (
        <div className="max-w-2xl">
          <div data-testid="report-latest-card" className="border border-gray-200 rounded-md p-5 mb-5">
            <p className="text-xs text-gray-400 mb-1" data-testid="report-week-range">
              {t("pi.rp_week", "Week of")} {latest.week_start_local} → {latest.week_end_local} · {latest.tz}
            </p>
            {latest.status === "insufficient_data" ? (
              <div data-testid="report-insufficient">
                <p className="text-lg font-heading font-bold text-gray-700">{t("pi.rp_low_title", "Not enough activity to measure accuracy")}</p>
                <p className="text-sm text-gray-500 mt-1">{t("pi.rp_low_desc", "{n} actions this week — need {min}. Nothing was changed.", { n: latest.sample, min: 20 })}</p>
              </div>
            ) : (
              <div className="flex items-baseline gap-3">
                <span data-testid="report-agreement" className={`text-3xl font-heading font-bold ${latest.agreement_pct === null ? "text-gray-400" : latest.agreement_pct >= 85 ? "text-emerald-600" : latest.agreement_pct >= 70 ? "text-amber-600" : "text-red-600"}`}>
                  {latest.agreement_pct !== null ? `${latest.agreement_pct}%` : "—"}
                </span>
                <span className="text-sm text-gray-500">{t("pi.rp_agreement", "agreement")} · {latest.sample} {t("pi.rp_actions", "actions")}</span>
                {latest.delta_vs_prev !== null && latest.delta_vs_prev !== undefined && (
                  <span data-testid="report-delta" className={`inline-flex items-center gap-0.5 text-sm font-semibold ${latest.delta_vs_prev >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {latest.delta_vs_prev >= 0 ? <TrendUp size={14} /> : <TrendDown size={14} />}
                    {latest.delta_vs_prev >= 0 ? "+" : ""}{latest.delta_vs_prev}% {t("pi.rp_vs_last", "vs last week")}
                  </span>
                )}
                {latest.status === "learning" && <span className="text-xs bg-blue-50 text-blue-700 font-semibold rounded-full px-2 py-0.5">{t("pi.rp_learning", "learning period (weeks 1–3, not judged)")}</span>}
                {latest.passed === true && <span className="text-xs bg-emerald-50 text-emerald-700 font-semibold rounded-full px-2 py-0.5">≥85% ✓</span>}
              </div>
            )}
            {(latest.why_improved || []).length > 0 && (
              <div data-testid="report-why-improved" className="mt-3 text-xs text-emerald-700">
                {latest.why_improved.map((s, i) => <p key={i}>▲ {s}</p>)}
              </div>
            )}
            {(latest.why_degraded || []).length > 0 && (
              <div data-testid="report-why-degraded" className="mt-3 text-xs text-red-600">
                {latest.why_degraded.map((s, i) => <p key={i}>▼ {s}</p>)}
              </div>
            )}
          </div>

          {history.length > 1 && (
            <div data-testid="report-history-bars" className="border border-gray-200 rounded-md p-4 mb-5">
              <p className="text-xs font-semibold text-gray-500 mb-2">{t("pi.rp_history", "Last 12 weeks")}</p>
              <div className="flex items-end gap-1.5 h-20">
                {history.map((r, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${r.week_start_local}: ${r.agreement_pct ?? "—"}%`}>
                    <div className={`w-full rounded-t ${r.agreement_pct === null || r.agreement_pct === undefined ? "bg-gray-200" : r.agreement_pct >= 85 ? "bg-emerald-500" : r.agreement_pct >= 70 ? "bg-amber-400" : "bg-red-400"}`}
                      style={{ height: `${Math.max(6, (r.agreement_pct || 0) * 0.7)}px` }} />
                    <span className="text-[9px] text-gray-400">{r.agreement_pct ?? "·"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(latest.applied || []).length > 0 && (
            <div data-testid="report-applied" className="border border-emerald-200 bg-emerald-50/40 rounded-md p-4 mb-3">
              <p className="text-xs font-bold text-emerald-700 mb-1.5">{t("pi.rp_applied", "APPLIED THIS WEEK")}</p>
              {latest.applied.map((a, i) => (
                <p key={i} className="text-xs text-gray-700">✅ {JSON.stringify(a.changes)} — {a.reason}</p>
              ))}
            </div>
          )}
          {(latest.rejected || []).length > 0 && (
            <div data-testid="report-rejected" className="border border-gray-200 rounded-md p-4 mb-3">
              <p className="text-xs font-bold text-gray-500 mb-1.5">{t("pi.rp_rejected", "REJECTED")}</p>
              {latest.rejected.map((r, i) => (
                <p key={i} className="text-xs text-gray-600">✖ {JSON.stringify(r.changes)} — {r.reason}</p>
              ))}
            </div>
          )}

          {(data.conflicts || []).length > 0 && (
            <div data-testid="report-conflicts" className="border border-amber-300 bg-amber-50/50 rounded-md p-4 mb-3">
              <p className="text-xs font-bold text-amber-700 mb-2">{t("pi.rp_conflicts", "CONFLICTS — you taught one thing but keep doing another")}</p>
              {data.conflicts.map((c) => (
                <div key={c.memory_id} data-testid={`report-conflict-${c.memory_id}`} className="mb-2">
                  <p className="text-sm text-gray-800">"{c.content}" — {t("pi.rp_conflict_desc", "but you archived {n} matching emails unread this week", { n: c.opposite_count })}</p>
                  <div className="flex gap-2 mt-1">
                    <button data-testid={`conflict-keep-${c.memory_id}`} onClick={() => resolveConflict(c.memory_id, "keep")}
                      className="inline-flex items-center gap-1 text-xs font-semibold border border-gray-300 hover:bg-white rounded-md px-2.5 py-1"><Check size={12} /> {t("pi.rp_keep", "Keep")}</button>
                    <button data-testid={`conflict-remove-${c.memory_id}`} onClick={() => resolveConflict(c.memory_id, "remove")}
                      className="inline-flex items-center gap-1 text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50 rounded-md px-2.5 py-1"><XIcon size={12} /> {t("pi.rp_remove", "Remove")}</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {(latest.worst_misses || []).length > 0 && latest.status !== "insufficient_data" && (
            <div data-testid="report-misses" className="border border-gray-200 rounded-md p-4">
              <p className="text-xs font-bold text-gray-500 mb-1.5">{t("pi.rp_misses", "WORST MISSES")}</p>
              {latest.worst_misses.map((m, i) => (
                <p key={i} data-testid={`report-miss-${i}`} className="text-xs text-gray-600 py-0.5">
                  {m.from_name || m.from_email} — {m.subject?.slice(0, 60)} · {t("pi.rp_scored", "scored")} {m.score ?? "—"} ({m.type}){m.weight > 1 ? " · VIP ×3" : ""}
                </p>
              ))}
              <p className="text-[11px] text-gray-400 mt-2">{t("pi.rp_miss_hint", "Use the Teach box on the Priority page to fix a recurring miss in one sentence.")}</p>
            </div>
          )}
        </div>
      )}
    </PageShell>
  );
}
